import React, { useEffect } from 'react';
import { LinearProgress, Typography } from '@mui/material';
import PageTabs from '../../tool/doc/PageTabs';
import ReviewPage from './ReviewPage';
import { FixMe } from '../../../types/util';
import { IDocument, IExtraction, IToken } from '../../../types/my-types';
import { MarkStateContext, MarkDispatchContext } from '../../../context/MarkContext';
import { styled } from '@mui/system';
import { useExtractionCorrected } from '../../hooks/useExtractionCorrected';
import { useParams } from 'react-router';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';

const DivRoot = styled('div')({
  flexGrow: 1,
  width: '100%',
});

const getMarkedTokens = (extractionResult: FixMe) => {
  let markedTokens: IToken[] = [];
  // transform extraction results to array of tokens with field label added
  if (Object.keys(extractionResult).length !== 0) {
    const entries = Object.entries(extractionResult);
    if (entries) {
      markedTokens = entries
        .map((entry) => {
          const tokens = (entry[1] as FixMe).tokens;
          if (tokens) {
            return tokens.map((t: IToken) => ({
              ...t,
              field_label: entry[0],
            }));
          } else {
            return [];
          }
        })
        .reduce((acc, val) => acc.concat(val), []);
    }
  }
  return markedTokens;
};
export interface ReviewContentProps {
  doc: IDocument;
  extractionId: string;
  tokenPages: FixMe[];
}

const ReviewContent: React.FC<ReviewContentProps> = ({ doc, extractionId, tokenPages }) => {
  const { dpid, id, eid } = useParams<{ dpid: string; id: string; eid: string }>();

  const { pageIndex } = React.useContext(MarkStateContext);
  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);

  const { data: extraction, isLoading, isError } = useExtractionCorrected(extractionId);

  // Generating tokenPages can take some time, and when navigating the grid, we may try to access
  // a pageIndex for an extraction that hasn't loaded or doesn't exist. Let's say we are viewing
  // extraction 2, which has 2 pages, and we are viewing a field that has tokens assigned on page 2
  // (pageIndex = 1) - the document viewer is on page 2. If we then click the same field on
  // extraction 1, which only has 1 page. The app needs to be smart enough to handle this. In order
  // to manage this, we have now added the docId to the tokenPages response. When the extraction id
  // changes, we load that extraction data and then get the document id from the document url.
  // We can then compare the two to make sure the tokenPages we are looking at is for the right
  // document for the extraction we are viewing If we have a pageIndex that is out of bounds for the
  // tokenPages, we reset it to 0.
  const docIdInTokenPages = tokenPages && tokenPages.length > 0 ? tokenPages[0].docId : null;
  const documentIdFromExtraction = extraction ? getIdFromUrl(extraction.document) : null;
  useEffect(() => {
    // if we're not viewing the current tokenPages, let's wait for the data to be updated
    if (documentIdFromExtraction !== docIdInTokenPages) return;

    // If we have tokenPages, the extractionId matches the extraction id in the URL
    // and the pageIndex is out of bounds, reset the pageIndex to 0.
    if (
      tokenPages &&
      tokenPages.length > 0 &&
      (pageIndex < 0 || pageIndex >= tokenPages.length) &&
      extractionId === eid
    ) {
      // reset pageIndex to 0
      dispatch({
        type: 'PAGE_INDEX',
        payload: 0,
      });
    }
  }, [
    pageIndex,
    tokenPages,
    extractionId,
    eid,
    dispatch,
    documentIdFromExtraction,
    docIdInTokenPages,
    extraction,
  ]);

  if (isLoading || documentIdFromExtraction !== docIdInTokenPages || extractionId !== eid) {
    return <LinearProgress />;
  }

  if (isError) {
    return <div>Sorry, failed to load extraction results</div>;
  }

  const fields: string[] = extraction ? extraction.fields.concat(extraction.additional_fields) : [];

  const markedTokens = getMarkedTokens(extraction?.results);

  const pageReady = doc.pages && doc.pages.length > 0 && tokenPages[pageIndex];

  return (
    <DivRoot>
      <PageTabs
        pages={doc.pages}
        docLabel={doc.label}
        extractionId={extractionId}
        extraction={extraction}
        documentId={doc.id}
      />

      {pageReady ? (
        <Typography component="div">
          <ReviewPage
            pageUrl={doc.pages[pageIndex]}
            page={tokenPages[pageIndex]}
            markedTokens={markedTokens}
            extraction={extraction as IExtraction}
            fields={fields}
          />
        </Typography>
      ) : (
        <Typography style={{ color: 'orange' }}>
          GENERATING DOCUMENT PAGES... PLEASE CONTACT OUR GLYNT SUPPORT TEAM IF IT TOOK MORE THAN
          ONE MINUTE.
        </Typography>
      )}
    </DivRoot>
  );
};

export default ReviewContent;
